import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";

/**
 * DeleteDialog — Pattern 1: simple delete confirmation for a single item.
 * "Delete this {noun}?" / "This permanently removes this {noun}. Nothing else is affected."
 * Cancel = gray ghost outline, Delete = filled red, equal size.
 *
 * Props:
 *   open       — boolean
 *   onClose    — () => void
 *   onConfirm  — () => void
 *   noun       — the item word: "document", "receipt", "gift", etc. Default "entry"
 *   busy       — optional loading state
 *   testid     — optional data-testid prefix (default "delete-dialog")
 */
// `title` and `confirmLabel` exist because NOT EVERY REMOVAL IS A DELETION, and
// this box said otherwise. (ZEN-295.) Taking an inbox document off an expense
// destroys nothing — the document goes back to the Imports & Receipts list and
// can be attached elsewhere — so "Delete this document? This permanently removes
// it. This can't be undone." was three false statements in one box, on the very
// action two of this ticket's refusals tell an owner to perform. Both default to
// what every existing caller already says, so nothing else changes.
export function DeleteDialog({ open, onClose, onConfirm, noun = "entry", busy = false, testid = "delete-dialog", body, title, confirmLabel }) {
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-sm" data-testid={`${testid}-dialog`}>
        <DialogHeader>
          <DialogTitle>{title || `Delete this ${noun}?`}</DialogTitle>
          {/* NOT a repeat of the heading: a screen reader announces both, and
              hearing the same sentence twice is noise. It names the action the
              confirm button performs, which is the part a heading does not say. */}
          <DialogDescription className="sr-only">{title ? `Confirm: ${confirmLabel || "Delete"}` : `Confirm deletion of this ${noun}`}</DialogDescription>
        </DialogHeader>
        {body || (
          <>
            <p className="text-sm text-[#374151]">
              This <b>permanently</b> removes this {noun}. Nothing else is affected.
            </p>
            <p className="text-sm font-bold text-[#374151]">This can&apos;t be undone.</p>
          </>
        )}
        <DialogFooter className="mt-2 gap-2">
          <button
            onClick={onClose}
            disabled={busy}
            className="flex-1 rounded-md border border-[#C2C9D1] bg-white px-4 py-2 text-sm font-bold text-[#374151] hover:bg-[#F4F7FB] disabled:opacity-40"
            data-testid={`${testid}-cancel`}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={busy}
            className="flex-1 rounded-md bg-[#F01919] px-4 py-2 text-sm font-bold text-white hover:bg-[#c81414] disabled:opacity-40"
            data-testid={`${testid}-confirm`}
          >
            {busy ? `${confirmLabel || "Delete"}…` : (confirmLabel || "Delete")}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
